import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAsset, getAssetScanDetails } from '../../services/assetService';
import './AssetDossierGrid.css';

/**
 * Asset Dossier - Variation C: Card Grid
 *
 * Design Philosophy:
 * - Dashboard-style card grid layout
 * - Each card is a self-contained module
 * - Quick scan overview at a glance
 * - Cards can be expanded for details
 */

const AssetDossierGrid = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [enrichment, setEnrichment] = useState(null);
  const [scanDetails, setScanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const assetData = await getAsset(assetId);
        setAsset(assetData.asset);
        setEnrichment(assetData.enrichment);

        if (assetData.asset?.lastResultId) {
          const details = await getAssetScanDetails(assetId, assetData.asset.lastResultId);
          setScanDetails(details);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (assetId) {
      fetchData();
    }
  }, [assetId]);

  const toggleExpand = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  // Calculate vulnerability stats
  const getVulnStats = () => {
    if (!scanDetails?.fullResult) return { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 };

    const findings = scanDetails.fullResult.findings ||
      scanDetails.fullResult.stageResults?.vulnScanning?.findings || [];

    const stats = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    findings.forEach(f => {
      const sev = (f.severity || f.info?.severity || 'info').toLowerCase();
      if (stats[sev] !== undefined) stats[sev]++;
    });
    stats.total = findings.length;
    return stats;
  };

  const vulnStats = getVulnStats();
  const findings = scanDetails?.fullResult?.findings ||
    scanDetails?.fullResult?.stageResults?.vulnScanning?.findings || [];
  const subdomains = enrichment?.subdomains || [];
  const ports = scanDetails?.fullResult?.stageResults?.portScanning?.ports || [];
  const openPorts = ports.filter(p => p.state === 'open');

  if (loading) {
    return (
      <div className="dossier-grid-page">
        <div className="grid-loading">
          <div className="grid-loading-spinner"></div>
          <p>Loading asset intelligence...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dossier-grid-page">
        <div className="grid-error">
          <span className="error-icon">\u26A0</span>
          <p>{error}</p>
          <button onClick={() => navigate('/assets')}>Return to Assets</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dossier-grid-page">
      {/* Top Navigation Bar */}
      <nav className="grid-top-nav">
        <button className="nav-back-btn" onClick={() => navigate('/assets')}>
          \u2190 Back to Assets
        </button>
        <div className="nav-breadcrumb">
          <span className="breadcrumb-item">Assets</span>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{asset?.target}</span>
        </div>
        <div className="nav-actions">
          <button className="action-btn">Export Report</button>
          <button className="action-btn primary">Run New Scan</button>
        </div>
      </nav>

      {/* Header Card */}
      <header className="grid-header-card">
        <div className="header-left">
          <h1 className="header-target">{asset?.target}</h1>
          <div className="header-meta">
            <span className="meta-badge type">{asset?.assetType || 'domain'}</span>
            <span className="meta-text">
              Last scanned: {asset?.lastScannedAt ? new Date(asset.lastScannedAt).toLocaleDateString() : 'Never'}
            </span>
          </div>
        </div>
        <div className="header-right">
          <div className="risk-indicator">
            <span className="risk-label">Risk Level</span>
            <span className={`risk-value ${vulnStats.critical > 0 ? 'critical' : vulnStats.high > 0 ? 'high' : 'healthy'}`}>
              {vulnStats.critical > 0 ? 'CRITICAL' : vulnStats.high > 0 ? 'HIGH' : 'HEALTHY'}
            </span>
          </div>
        </div>
      </header>

      {/* Main Grid */}
      <main className="grid-container">
        {/* Severity Overview Card */}
        <div className={`grid-card severity-card ${expandedCard === 'severity' ? 'expanded' : ''}`}>
          <div className="card-header" onClick={() => toggleExpand('severity')}>
            <h3>
              <span className="card-icon">\u26A0</span>
              Vulnerability Summary
            </h3>
            <span className={`expand-icon ${expandedCard === 'severity' ? 'expanded' : ''}`}>\u25BC</span>
          </div>
          <div className="card-body">
            <div className="severity-stats">
              <div className="stat-item critical">
                <span className="stat-value">{vulnStats.critical}</span>
                <span className="stat-label">Critical</span>
              </div>
              <div className="stat-item high">
                <span className="stat-value">{vulnStats.high}</span>
                <span className="stat-label">High</span>
              </div>
              <div className="stat-item medium">
                <span className="stat-value">{vulnStats.medium}</span>
                <span className="stat-label">Medium</span>
              </div>
              <div className="stat-item low">
                <span className="stat-value">{vulnStats.low}</span>
                <span className="stat-label">Low</span>
              </div>
            </div>
            {expandedCard === 'severity' && (
              <div className="card-expanded-content">
                <div className="severity-bar">
                  {vulnStats.total > 0 ? (
                    <>
                      <div className="bar-segment critical" style={{ width: `${(vulnStats.critical / vulnStats.total) * 100}%` }}></div>
                      <div className="bar-segment high" style={{ width: `${(vulnStats.high / vulnStats.total) * 100}%` }}></div>
                      <div className="bar-segment medium" style={{ width: `${(vulnStats.medium / vulnStats.total) * 100}%` }}></div>
                      <div className="bar-segment low" style={{ width: `${(vulnStats.low / vulnStats.total) * 100}%` }}></div>
                      <div className="bar-segment info" style={{ width: `${(vulnStats.info / vulnStats.total) * 100}%` }}></div>
                    </>
                  ) : (
                    <div className="bar-empty">No vulnerabilities</div>
                  )}
                </div>
                <p className="expanded-note">
                  Total of {vulnStats.total} findings across all severity levels.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Attack Surface Card */}
        <div className={`grid-card surface-card ${expandedCard === 'surface' ? 'expanded' : ''}`}>
          <div className="card-header" onClick={() => toggleExpand('surface')}>
            <h3>
              <span className="card-icon">\u25CE</span>
              Attack Surface
            </h3>
            <span className={`expand-icon ${expandedCard === 'surface' ? 'expanded' : ''}`}>\u25BC</span>
          </div>
          <div className="card-body">
            <div className="surface-metrics">
              <div className="metric">
                <span className="metric-value">{subdomains.length}</span>
                <span className="metric-label">Subdomains</span>
              </div>
              <div className="metric">
                <span className="metric-value">{openPorts.length}</span>
                <span className="metric-label">Open Ports</span>
              </div>
              <div className="metric">
                <span className="metric-value">{enrichment?.technologies?.length || 0}</span>
                <span className="metric-label">Technologies</span>
              </div>
            </div>
            {expandedCard === 'surface' && (
              <div className="card-expanded-content">
                {subdomains.length > 0 && (
                  <div className="expanded-section">
                    <h4>Top Subdomains</h4>
                    <ul className="mini-list">
                      {subdomains.slice(0, 5).map((sub, idx) => (
                        <li key={idx}>{sub.host || sub}</li>
                      ))}
                      {subdomains.length > 5 && <li className="more">+{subdomains.length - 5} more</li>}
                    </ul>
                  </div>
                )}
                {openPorts.length > 0 && (
                  <div className="expanded-section">
                    <h4>Open Ports</h4>
                    <div className="port-chips">
                      {openPorts.slice(0, 8).map((port, idx) => (
                        <span key={idx} className="port-chip">{port.port}/{port.service || 'unknown'}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Top Vulnerabilities Card */}
        <div className={`grid-card vulns-card ${expandedCard === 'vulns' ? 'expanded' : ''}`}>
          <div className="card-header" onClick={() => toggleExpand('vulns')}>
            <h3>
              <span className="card-icon">\u2620</span>
              Top Vulnerabilities
            </h3>
            <span className={`expand-icon ${expandedCard === 'vulns' ? 'expanded' : ''}`}>\u25BC</span>
          </div>
          <div className="card-body">
            {findings.length > 0 ? (
              <div className="vuln-list">
                {findings
                  .sort((a, b) => {
                    const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
                    const sevA = (a.severity || a.info?.severity || 'info').toLowerCase();
                    const sevB = (b.severity || b.info?.severity || 'info').toLowerCase();
                    return order[sevA] - order[sevB];
                  })
                  .slice(0, expandedCard === 'vulns' ? 10 : 4)
                  .map((finding, idx) => {
                    const severity = (finding.severity || finding.info?.severity || 'info').toLowerCase();
                    return (
                      <div key={idx} className="vuln-item">
                        <span className={`vuln-severity ${severity}`}>{severity.charAt(0).toUpperCase()}</span>
                        <span className="vuln-name">{finding.info?.name || finding.templateId || 'Unknown'}</span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="no-vulns-card">
                <span className="check-icon">\u2713</span>
                <span>No vulnerabilities detected</span>
              </div>
            )}
            {expandedCard === 'vulns' && findings.length > 10 && (
              <p className="expanded-note">Showing top 10 of {findings.length} total findings.</p>
            )}
          </div>
        </div>

        {/* Quick Actions Card */}
        <div className="grid-card actions-card">
          <div className="card-header">
            <h3>
              <span className="card-icon">\u26A1</span>
              Quick Actions
            </h3>
          </div>
          <div className="card-body">
            <div className="action-buttons">
              <button className="quick-action-btn">
                <span className="btn-icon">\u21BB</span>
                Rescan Asset
              </button>
              <button className="quick-action-btn">
                <span className="btn-icon">\u2193</span>
                Export PDF
              </button>
              <button className="quick-action-btn">
                <span className="btn-icon">\u2606</span>
                Add to Watchlist
              </button>
              <button className="quick-action-btn">
                <span className="btn-icon">\u2709</span>
                Share Report
              </button>
            </div>
          </div>
        </div>

        {/* Testing Approach Card */}
        <div className={`grid-card testing-card ${expandedCard === 'testing' ? 'expanded' : ''}`}>
          <div className="card-header" onClick={() => toggleExpand('testing')}>
            <h3>
              <span className="card-icon">\u2697</span>
              Testing Approach
            </h3>
            <span className={`expand-icon ${expandedCard === 'testing' ? 'expanded' : ''}`}>\u25BC</span>
          </div>
          <div className="card-body">
            <div className="testing-phases">
              <div className="phase">
                <span className="phase-num">1</span>
                <span className="phase-name">Reconnaissance</span>
              </div>
              <div className="phase">
                <span className="phase-num">2</span>
                <span className="phase-name">Discovery</span>
              </div>
              <div className="phase">
                <span className="phase-num">3</span>
                <span className="phase-name">Assessment</span>
              </div>
              <div className="phase">
                <span className="phase-num">4</span>
                <span className="phase-name">Validation</span>
              </div>
            </div>
            {expandedCard === 'testing' && (
              <div className="card-expanded-content">
                <div className="phase-details">
                  <div className="phase-detail">
                    <h4>1. Reconnaissance</h4>
                    <p>Subdomain enumeration, DNS analysis, technology fingerprinting</p>
                  </div>
                  <div className="phase-detail">
                    <h4>2. Service Discovery</h4>
                    <p>Port scanning, service identification, version detection</p>
                  </div>
                  <div className="phase-detail">
                    <h4>3. Vulnerability Assessment</h4>
                    <p>CVE scanning, misconfigurations, security header analysis</p>
                  </div>
                  <div className="phase-detail">
                    <h4>4. Validation</h4>
                    <p>Safe validation tests, impact assessment, proof of concept</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Defense Strategy Card */}
        <div className={`grid-card defense-card ${expandedCard === 'defense' ? 'expanded' : ''}`}>
          <div className="card-header" onClick={() => toggleExpand('defense')}>
            <h3>
              <span className="card-icon">\u2610</span>
              Defense Strategy
            </h3>
            <span className={`expand-icon ${expandedCard === 'defense' ? 'expanded' : ''}`}>\u25BC</span>
          </div>
          <div className="card-body">
            <div className="priority-items">
              {vulnStats.critical > 0 && (
                <div className="priority-item critical">
                  <span className="priority-marker"></span>
                  <span className="priority-text">Address {vulnStats.critical} critical findings immediately</span>
                </div>
              )}
              {vulnStats.high > 0 && (
                <div className="priority-item high">
                  <span className="priority-marker"></span>
                  <span className="priority-text">Remediate {vulnStats.high} high severity issues</span>
                </div>
              )}
              <div className="priority-item info">
                <span className="priority-marker"></span>
                <span className="priority-text">Schedule regular monitoring scans</span>
              </div>
            </div>
            {expandedCard === 'defense' && (
              <div className="card-expanded-content">
                <h4>Recommendations</h4>
                <ul className="recommendations">
                  <li>Implement web application firewall (WAF)</li>
                  <li>Enable security headers (CSP, HSTS)</li>
                  <li>Keep software and dependencies updated</li>
                  <li>Regular penetration testing</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Technologies Card */}
        {enrichment?.technologies && enrichment.technologies.length > 0 && (
          <div className="grid-card tech-card">
            <div className="card-header">
              <h3>
                <span className="card-icon">\u2699</span>
                Technologies
              </h3>
            </div>
            <div className="card-body">
              <div className="tech-grid">
                {enrichment.technologies.slice(0, 8).map((tech, idx) => (
                  <div key={idx} className="tech-item">
                    <span className="tech-name">{tech.name || tech}</span>
                    {tech.version && <span className="tech-version">v{tech.version}</span>}
                  </div>
                ))}
              </div>
              {enrichment.technologies.length > 8 && (
                <p className="more-tech">+{enrichment.technologies.length - 8} more technologies</p>
              )}
            </div>
          </div>
        )}

        {/* Scan History Card */}
        <div className="grid-card history-card">
          <div className="card-header">
            <h3>
              <span className="card-icon">\u23F0</span>
              Scan History
            </h3>
          </div>
          <div className="card-body">
            <div className="history-item">
              <span className="history-date">
                {asset?.lastScannedAt ? new Date(asset.lastScannedAt).toLocaleString() : 'N/A'}
              </span>
              <span className="history-type">Full Scan</span>
              <span className="history-findings">{vulnStats.total} findings</span>
            </div>
            <div className="history-note">
              View full scan history in the Reports section
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="grid-footer">
        <p>Asset Dossier - Grid View | DriftAlarm</p>
        <p className="footer-proto">UX Prototype - Variation C</p>
      </footer>
    </div>
  );
};

export default AssetDossierGrid;
